import { uniqueId } from '../../../helpers/unique-id';

/**
 * ## Tree Data Management Functions

 This module provides functions for managing a tree data structure. The data structure is represented by an array of objects, where each object can have an `id`, `parentId` (ID of its parent node), and any additional data specific to the item.

 These functions allow you to add, update, and delete items within the tree structure while maintaining its integrity.
 */

/**
 * Adds a new item to the tree structure.
 *
 * @param {Array} data - The current data array representing the tree structure.
 * @param {string|null} parentId - The ID of the parent node. If `null`, the new item is added as a root node.
 * @param {Object} initialData - Additional data for the new item (e.g., name).
 * @returns {Array} - A new data array with the added item.
 * @returns {idField} - idField, default is id
 *
 * @throws {Error} - Throws an error if the parent ID is not found.
 */
export function addItem(
  data = [],
  parentId = null,
  initialData = {},
  idField = 'id'
) {
  const item = {
    [idField]: uniqueId(),
    parent: parentId,
    ...initialData,
  };

  const newData = [...data, item];

  // Add as root item
  if (!parentId) {
    return newData;
  }

  const parentIndex = newData.findIndex((d) => d[idField] === parentId);

  if (parentIndex < 0) {
    throw new Error('No parent found with the provided ID');
  }

  if (!newData[parentIndex].children) {
    newData[parentIndex].children = [];
  }

  newData[parentIndex].children.push(item[idField]);

  return newData;
}

/**
 * Updates an existing item in the tree structure.
 *
 * @param {Array} data - The current data array representing the tree structure.
 * @param {string} idToUpdate - The ID of the item to update.
 * @param {Object} updates - The new data to merge with the existing item data.
 * @returns {Array} - A new data array with the updated item.
 * @returns {idField} - idField, default is id
 *
 * @throws {Error} - Throws an error if the item to update is not found.
 */
export function updateItem(
  data = [],
  idToUpdate,
  updates = {},
  idField = 'id'
) {
  const newData = [...data];

  if (!idToUpdate) {
    throw new Error('No parent ID provided for updateItem');
  }

  const itemIndex = newData.findIndex((item) => item[idField] === idToUpdate);

  if (itemIndex < 0) {
    return newData;
  }

  const { jId, ...restProps } = updates;

  // Update data (potentially more efficient)
  newData[itemIndex] = {
    ...newData[itemIndex],
    ...restProps,
  };

  if (!newData[itemIndex].jId) {
    newData[itemIndex].jId = jId;
  }

  return newData;
}

/**
 * Deletes an item and its children from the tree structure.
 *
 * @param {Array} data - The current data array representing the tree structure.
 * @param {string} idToDelete - The ID of the item to delete.
 * @returns {Array} - A new data array without the deleted item and its children.
 * @returns {idField} - idField, default is id
 *
 * @throws {Error} - Throws an error if no ID is provided for deletion.
 */
export function deleteItem(data = [], idToDelete, idField = 'id') {
  if (!idToDelete) {
    throw new Error('No ID provided for deletion');
  }

  // Helper function to find a node by its ID
  const findNodeById = (id) => data.find((node) => node[idField] === id);

  // Recursive function to gather all node IDs to delete (including children)
  const gatherNodeAndChildrenIds = (id) => {
    const node = findNodeById(id);
    if (!node) return [];

    let idsToDelete = [id]; // Start with the current node's ID

    // If the node has children, recursively gather their IDs too
    if (node.children) {
      node.children.forEach((childId) => {
        idsToDelete = [...idsToDelete, ...gatherNodeAndChildrenIds(childId)];
      });
    }

    return idsToDelete;
  };

  // Gather all IDs to delete
  const idsToDelete = gatherNodeAndChildrenIds(idToDelete);

  // Create a new data array excluding the nodes to be deleted
  const newData = data.filter((node) => !idsToDelete.includes(node[idField]));

  // clean children array
  for (let index = 0; index < newData.length; index++) {
    if (newData[index].children) {
      newData[index].children = newData[index].children.filter(
        (item) => !idsToDelete.includes(item)
      );
    }
  }

  return newData;
}

/**
 * Deletes all children and their descendants of a specified node in the tree structure.
 *
 * @param {Array} data - The current data array representing the tree structure.
 * @param {string} parentId - The ID of the parent node whose children should be deleted.
 * @returns {Array} - A new data array without the deleted children and their descendants.
 * @returns {idField} - idField, default is id
 *
 * @throws {Error} - Throws an error if no parent ID is provided for deletion.
 */
export function deleteChildren(data = [], parentId, idField = 'id') {
  if (!parentId) {
    throw new Error('No parent ID provided for deletion');
  }

  const childrenToDelete = [];

  function childFinder(parentIdTarget) {
    const children = data.filter((item) => item?.parent === parentIdTarget);

    if (children.length) {
      for (let index = 0; index < children.length; index++) {
        const element = children[index];

        childrenToDelete.push(element[idField]);

        childFinder(element[idField]);
      }
    }
  }

  childFinder(parentId);

  const dataToManipulate = [...data];

  // Create a new data array excluding the nodes to be deleted
  const newData = dataToManipulate.filter(
    (node) => !childrenToDelete.includes(node[idField])
  );

  // clean children array
  for (let index = 0; index < newData.length; index++) {
    if (newData[index].children) {
      const newChildren = newData[index].children.filter(
        (item) => !childrenToDelete.includes(item)
      );

      if (!newChildren.length) {
        delete newData[index].children;
      } else {
        newData[index].children = [...newChildren];
      }
    }
  }

  // console.log(data, newData, parentId);

  return [...newData];
}
